/**
 * Manual pair builder — operator picks account slot A + account slot B
 * from a rich list (firm logo, challenge type, phase, size, platform,
 * DDs, target) and force-pairs them. Client-side delta view mirrors
 * the server's classifyQuality logic so the operator sees whether the
 * pair is "perfect" before committing.
 *
 * Replaces the previous bare <select> which only showed `phase` (looked
 * like "1-step" for every account because every account starts on
 * step1). Challenge-type and phase are separate fields — this component
 * renders both explicitly so they can't be confused.
 */

import { useMemo, useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FirmAvatar } from "@/components/firm-logo";
import { firmLogoUrl } from "@/lib/firm-logos";
import { Loader2, AlertTriangle, Check } from "@/components/ui/icons";
import { forcePair } from "@/lib/api/pair-proposals";
import { toast } from "sonner";
import { useConfirm } from "@/components/ui/dialog-imperative";
import { cn } from "@/lib/utils";
import type { AccountRow } from "@/lib/api/accounts";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accounts: AccountRow[];
  onPaired: (result: { accountPairSlug: string | null }) => void;
}

interface FieldRow {
  label: string;
  a: string | number | null;
  b: string | number | null;
  match: boolean;
}

const NUMERIC: Array<{ key: keyof AccountRow; label: string }> = [
  { key: "account_size", label: "Account Size" },
  { key: "daily_loss_pct", label: "Daily Loss %" },
  { key: "max_drawdown_pct", label: "Max DD %" },
  { key: "profit_target_pct", label: "Profit Target %" },
];

const CATEGORICAL: Array<{ key: keyof AccountRow; label: string }> = [
  { key: "challenge_type", label: "Challenge Type" },
  { key: "daily_dd_type", label: "Daily DD Type" },
  { key: "max_dd_type", label: "Max DD Type" },
  { key: "phase", label: "Phase" },
];

function fmt(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return String(v);
  return String(v);
}

function firmName(firmId: string): string {
  return firmId
    .split(/[-_ ]+/)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
}

function phaseLabel(phase: string | null | undefined): string {
  if (!phase) return "unknown phase";
  if (phase === "funded") return "Funded";
  if (phase === "step1") return "Step 1";
  if (phase === "step2") return "Step 2";
  if (phase === "step3") return "Step 3";
  return phase;
}

function platformLabel(p: string | null | undefined): string {
  if (!p) return "—";
  if (p === "mt5") return "MT5";
  if (p === "mt4") return "MT4";
  if (p === "tradelocker") return "TradeLocker";
  if (p === "match-trader") return "Match-Trader";
  if (p === "ctrader") return "cTrader";
  return p;
}

/** A single account card in the picker list — rich display, clickable. */
function AccountCard({
  account,
  selected,
  disabled,
  disabledReason,
  onPick,
}: {
  account: AccountRow;
  selected: boolean;
  disabled: boolean;
  disabledReason?: string;
  onPick: () => void;
}): ReactNode {
  const logo = firmLogoUrl(account.firm_id);
  const size = account.account_size;
  const dd = account.daily_loss_pct;
  const max = account.max_drawdown_pct;
  const target = account.profit_target_pct;
  const platform = platformLabel(account.platform_type);
  const cType = account.challenge_type;
  const phase = account.phase;

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onPick}
      disabled={disabled}
      title={disabledReason}
      className={cn(
        "group flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors",
        selected
          ? "border-accent bg-accent/10"
          : "border-border/60 bg-background hover:bg-muted/40",
        disabled && "cursor-not-allowed opacity-50 hover:bg-background",
      )}
    >
      <FirmAvatar
        src={logo ?? ""}
        name={firmName(account.firm_id)}
        size="sm"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium text-foreground">
            {firmName(account.firm_id)}
          </span>
          <span className="font-mono text-[10px] text-muted-foreground">
            {account.id}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          {cType ? (
            <Badge size="xs" variant="outline">
              {cType}
            </Badge>
          ) : (
            <Badge size="xs" variant="warning">
              no challenge-type
            </Badge>
          )}
          <Badge size="xs" variant="secondary">
            {phaseLabel(phase)}
          </Badge>
          <Badge size="xs" variant="secondary">
            {platform}
          </Badge>
          {account.status !== "active" && (
            <Badge size="xs" variant="warning">
              {account.status}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[11px] text-muted-foreground">
          <span>
            {size != null ? `$${size.toLocaleString()}` : "size —"}
          </span>
          <span>Daily {dd != null ? `${dd}%` : "—"}</span>
          <span>Max {max != null ? `${max}%` : "—"}</span>
          <span>Target {target != null ? `${target}%` : "—"}</span>
        </div>
      </div>
      {selected && <Check className="mt-0.5 size-4 shrink-0 text-accent" />}
    </button>
  );
}

export function ManualPairDialog({
  open,
  onOpenChange,
  accounts,
  onPaired,
}: Props): ReactNode {
  const confirm = useConfirm();
  const eligible = useMemo(
    () =>
      accounts.filter(
        (a) =>
          a.pair_id == null &&
          a.status !== "closed" &&
          a.status !== "failed_validation",
      ),
    [accounts],
  );

  const [longId, setLongId] = useState<string>("");
  const [shortId, setShortId] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  const long = eligible.find((a) => a.id === longId) ?? null;
  const short = eligible.find((a) => a.id === shortId) ?? null;

  const fieldRows: FieldRow[] = useMemo(() => {
    if (!long || !short) return [];
    const rows: FieldRow[] = [];
    for (const { key, label } of NUMERIC) {
      const a = long[key] as number | null;
      const b = short[key] as number | null;
      const match = a != null && b != null && a === b;
      rows.push({ label, a, b, match });
    }
    for (const { key, label } of CATEGORICAL) {
      const a = long[key] as string | null;
      const b = short[key] as string | null;
      const match = a != null && b != null && a === b;
      rows.push({ label, a, b, match });
    }
    return rows;
  }, [long, short]);

  const liveQuality = useMemo<"perfect" | "near" | "mismatch" | null>(() => {
    if (!long || !short) return null;
    if (long.firm_id === short.firm_id) return "mismatch";
    if (fieldRows.every((r) => r.match)) return "perfect";
    const byLabel = new Map(fieldRows.map((r) => [r.label, r]));
    const sizeMatch = byLabel.get("Account Size")?.match === true;
    const catsMatch = CATEGORICAL.every(
      ({ label }) => byLabel.get(label)?.match === true,
    );
    const daily = byLabel.get("Daily Loss %");
    const max = byLabel.get("Max DD %");
    const within = (r: FieldRow | undefined): boolean => {
      if (!r || r.match) return true;
      if (typeof r.a === "number" && typeof r.b === "number") {
        return Math.abs(r.a - r.b) <= 1;
      }
      return false;
    };
    if (sizeMatch && catsMatch && within(daily) && within(max)) return "near";
    return "mismatch";
  }, [long, short, fieldRows]);

  const sameFirm = long && short && long.firm_id === short.firm_id;
  const sameAccount = long && short && long.id === short.id;
  const canSubmit = long && short && !sameFirm && !sameAccount && !submitting;

  const onConfirm = async (): Promise<void> => {
    if (!long || !short) return;
    if (liveQuality !== "perfect") {
      const ok = await confirm({
        title: `Force This "${liveQuality}" Pair?`,
        description:
          "The accounts differ on at least one rule. In a hedge, the tighter-constrained side breaches before the looser side hits its target. If you force this pair, you accept the asymmetry.",
        confirmText: "Force Pair",
        destructive: true,
      });
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      const res = await forcePair({
        longAccountId: long.id,
        shortAccountId: short.id,
        acknowledgedDeltas: true,
        reason: reason.trim() || undefined,
      });
      toast.success(
        liveQuality === "perfect"
          ? `Paired ${firmName(long.firm_id)} ↔ ${firmName(short.firm_id)} (starts paused for setup)`
          : `Force-paired ${firmName(long.firm_id)} ↔ ${firmName(short.firm_id)} (${liveQuality}, starts paused)`,
      );
      onPaired({ accountPairSlug: res.result.accountPairSlug });
      onOpenChange(false);
      setLongId("");
      setShortId("");
      setReason("");
    } catch (e) {
      toast.error(
        `Pairing failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Build A Pair Manually</DialogTitle>
          <DialogDescription>
            Pick Account A And Account B. Same-Firm Hedging Is Not Supported — The Two Slots Must Come From Different Firms. New Pairs Start Paused So You Can Finish Setup Before Trading.
          </DialogDescription>
        </DialogHeader>

        {eligible.length < 2 ? (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-sm text-amber-500">
            Need At Least Two Unpaired Accounts — Currently {eligible.length}.
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">
                    Account A
                  </span>
                  {long && (
                    <button
                      type="button"
                      onClick={() => setLongId("")}
                      className="text-[10px] text-muted-foreground hover:text-foreground"
                    >
                      clear
                    </button>
                  )}
                </div>
                <div className="flex max-h-64 flex-col gap-1.5 overflow-y-auto pr-1">
                  {eligible.map((a) => {
                    const disabled = a.id === shortId;
                    return (
                      <AccountCard
                        key={a.id}
                        account={a}
                        selected={a.id === longId}
                        disabled={disabled}
                        disabledReason={
                          disabled ? "already selected as Account B" : undefined
                        }
                        onPick={() => setLongId(a.id)}
                      />
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">
                    Account B
                  </span>
                  {short && (
                    <button
                      type="button"
                      onClick={() => setShortId("")}
                      className="text-[10px] text-muted-foreground hover:text-foreground"
                    >
                      clear
                    </button>
                  )}
                </div>
                <div className="flex max-h-64 flex-col gap-1.5 overflow-y-auto pr-1">
                  {eligible.map((a) => {
                    const disabled = a.id === longId;
                    return (
                      <AccountCard
                        key={a.id}
                        account={a}
                        selected={a.id === shortId}
                        disabled={disabled}
                        disabledReason={
                          disabled ? "already selected as Account A" : undefined
                        }
                        onPick={() => setShortId(a.id)}
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            {long && short && (
              <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">
                    Compatibility
                  </span>
                  {liveQuality && (
                    <Badge
                      variant={
                        liveQuality === "perfect"
                          ? "passed"
                          : liveQuality === "near"
                            ? "warning"
                            : "breached"
                      }
                      size="xs"
                    >
                      {liveQuality === "perfect" ? (
                        <>
                          <Check className="size-3" /> Perfect
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="size-3" />
                          {liveQuality === "near" ? "Near" : "Mismatch"}
                        </>
                      )}
                    </Badge>
                  )}
                </div>
                {sameFirm && (
                  <div className="mb-2 rounded border border-destructive/40 bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
                    Both Accounts Are On The Same Firm — Hedge Must Cross Firms.
                  </div>
                )}
                <div className="overflow-hidden rounded-md">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border/40 text-left text-muted-foreground">
                        <th className="px-2 py-1.5 font-medium">Field</th>
                        <th className="px-2 py-1.5 font-medium">Account A</th>
                        <th className="px-2 py-1.5 font-medium">Account B</th>
                        <th className="w-8 px-2 py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {fieldRows.map((r) => (
                        <tr
                          key={r.label}
                          className={
                            r.match
                              ? ""
                              : "bg-amber-500/10 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400"
                          }
                        >
                          <td className="px-2 py-1 text-muted-foreground">
                            {r.label}
                          </td>
                          <td className="px-2 py-1 font-mono">{fmt(r.a)}</td>
                          <td className="px-2 py-1 font-mono">{fmt(r.b)}</td>
                          <td className="px-2 py-1 text-center">
                            {r.match ? (
                              <Check className="mx-auto size-3 text-emerald-500" />
                            ) : (
                              <AlertTriangle className="mx-auto size-3 text-amber-500" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="text-xs text-muted-foreground">
                Reason <span className="text-muted-foreground/60">(Optional — Why Force This Non-Perfect Pair)</span>
              </span>
              <input
                type="text"
                value={reason}
                maxLength={500}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. manual batch assembly before rules are finalized"
                className="h-8 rounded-lg border border-border bg-background px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
              />
            </label>

            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button onClick={() => void onConfirm()} disabled={!canSubmit}>
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" /> Pairing…
                  </>
                ) : (
                  "Create Pair"
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
